import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { catchError, finalize, map } from "rxjs/operators";
import { AbstractService } from "../../abstractions/abstract-typed.service";
import { Globals } from "../../app.globals";

@Injectable()
export class SubContractorsService extends AbstractService<SubContractor> {
    private apiUrl = "/api/settings/Subcontractors";

    constructor(public http: HttpClient, public globals: Globals) {
        super(http, globals);
    }

    getAllOrdered(projectId: number): Observable<SubContractor[]> {
        this.showSpinner();
        return this.http
            .get(this.getApiUrl() + "/" + projectId + "/getallordered/")
            .pipe(
                map((res) => res as SubContractor[]),
                catchError(this.handleError),
                finalize(() => this.globals.hideSpinner())
            );
    }

    getAllAgencies(): Observable<SubContractor[]> {
        this.showSpinner();
        return this.http.get(this.getApiUrl() + "/getallagencies/").pipe(
            map((res) => res as SubContractor[]),
            catchError(this.handleError),
            finalize(() => this.globals.hideSpinner())
        );
    }

    getAllAgenciesWithProjectId(): Observable<SubContractor[]> {
        this.showSpinner();
        return this.http
            .get(this.getApiUrl() + "/getallagencieswithprojectid/")
            .pipe(
                map((res) => res as SubContractor[]),
                catchError(this.handleError),
                finalize(() => this.globals.hideSpinner())
            );
    }

    getCode(): Observable<any> {
        this.showSpinner();
        return this.http
            .get(this.getApiUrl() + "/code", { responseType: "text" })
            .pipe(
                map((res) => res),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    getApiUrl(): string {
        return this.apiUrl;
    }

    getDocuments(projectId: number, orderId: number): Observable<Document[]> {
        this.showSpinner();
        return this.http
            .get(this.getApiUrl() + "/" + projectId + "/document/" + orderId)
            .pipe(
                map((res) => res as Document[]),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    getDocumentsPayment(
        projectId: number,
        paymentid: number
    ): Observable<Document[]> {
        this.showSpinner();
        return this.http
            .get(
                this.getApiUrl() + "/" + projectId + "/paymentDocs/" + paymentid
            )
            .pipe(
                map((res) => res as Document[]),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    deleteFile(fileName): Observable<void> {
        this.showSpinner();
        return this.http
            .request("delete", this.getApiUrl() + "/file/delete", {
                body: { url: fileName },
            })
            .pipe(
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    getOrders(): Observable<Order[]> {
        this.showSpinner();
        return this.http
            .get(this.getApiUrl() + "/" + this.projectId + "/orders")
            .pipe(
                map((res) => res as Order[]),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    getOrdersUniqueSubcontractors(): Observable<Order[]> {
        this.showSpinner();
        return this.http
            .get(this.getApiUrl() + "/" + this.projectId + "/orders/unique")
            .pipe(
                map((res) => res as Order[]),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    getOrder(id: number): Observable<Order> {
        this.showSpinner();
        return this.http
            .get(this.getApiUrl() + "/" + this.projectId + "/order/" + id)
            .pipe(
                map((res) => res as Order),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    getOrderFromPackageAndSubcontractor(
        packageId: number,
        subcontractorId: number
    ): Observable<Order> {
        this.showSpinner();
        return this.http
            .get(
                this.getApiUrl() +
                    "/" +
                    this.projectId +
                    "/order/" +
                    packageId +
                    "/" +
                    subcontractorId
            )
            .pipe(
                map((res) => res as Order),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    isPackageLookupInUse(packageId: string): Observable<boolean> {
        this.showSpinner();
        return this.http
            .get(
                this.getApiUrl() +
                    "/" +
                    this.projectId +
                    "/packageLookupInUse/" +
                    packageId
            )
            .pipe(
                map((res) => res as boolean),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    isPaymentTermLookupInUse(paymentTerm: string): Observable<boolean> {
        this.showSpinner();
        return this.http
            .get(
                this.getApiUrl() +
                    "/" +
                    this.projectId +
                    "/paymentTermLookupInUse/" +
                    paymentTerm
            )
            .pipe(
                map((res) => res as boolean),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    saveOrder(entity: Order): Observable<any> {
        this.showSpinner();
        entity = this.mapSave(entity);
        return this.http
            .post(
                this.getApiUrl() + "/" + this.projectId + "/order",
                Order.map(entity)
            )
            .pipe(
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    deleteOrder(id: number): Observable<void> {
        this.showSpinner();
        return this.http
            .delete(this.getApiUrl() + "/" + this.projectId + "/order/" + id)
            .pipe(
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    getPayments(): Observable<Payment[]> {
        this.showSpinner();
        return this.http
            .get(this.getApiUrl() + "/" + this.projectId + "/Payments")
            .pipe(
                map((res) => res as Payment[]),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    getPaymentsAccumulatedBeforeId(id: number): Observable<Payment[]> {
        this.showSpinner();
        return this.http
            .get(
                this.getApiUrl() +
                    "/" +
                    this.projectId +
                    "/PaymentsAccumulated/" +
                    id
            )
            .pipe(
                map((res) => res as Payment[]),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    getPaymentsAccumulated(
        subcontractorid: number,
        packageId: string
    ): Observable<Payment[]> {
        this.showSpinner();
        return this.http
            .get(
                this.getApiUrl() +
                    "/" +
                    this.projectId +
                    "/PaymentsAccumulated/" +
                    subcontractorid +
                    "/" +
                    packageId
            )
            .pipe(
                map((res) => res as Payment[]),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    getPayment(id: number): Observable<Payment> {
        this.showSpinner();
        return this.http
            .get(this.getApiUrl() + "/" + this.projectId + "/Payment/" + id)
            .pipe(
                map((res) => res as Payment),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    savePayment(entity: Payment): Observable<any> {
        this.showSpinner();
        return this.http
            .post(
                this.getApiUrl() + "/" + this.projectId + "/Payment",
                Payment.map(entity)
            )
            .pipe(
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    deletePayment(id: number): Observable<void> {
        this.showSpinner();
        return this.http
            .delete(this.getApiUrl() + "/" + this.projectId + "/Payment/" + id)
            .pipe(
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    getPackages(section: string): Observable<Package[]> {
        this.showSpinner();
        return this.http
            .get(
                this.getApiUrl() +
                    "/" +
                    this.projectId +
                    "/" +
                    section +
                    "/Packages"
            )
            .pipe(
                map((res) => res as Package[]),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    getPackage(id: number, securityCode: string): Observable<Package> {
        this.showSpinner();
        return this.http
            .get(
                this.getApiUrl() +
                    "/" +
                    this.projectId +
                    "/Package/" +
                    id +
                    "/" +
                    securityCode
            )
            .pipe(
                map((res) => res as Package),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    savePackage(entity: Package, section: string): Observable<any> {
        this.showSpinner();
        return this.http
            .post(
                this.getApiUrl() + "/" + this.projectId + "/Package/" + section,
                Package.map(entity)
            )
            .pipe(
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    saveReturnPackage(entity: Package): Observable<any> {
        this.showSpinner();
        return this.http
            .post(
                this.getApiUrl() + "/" + this.projectId + "/returnPackage",
                entity
            )
            .pipe(
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    deletePackage(id: number): Observable<void> {
        this.showSpinner();
        return this.http
            .delete(this.getApiUrl() + "/" + this.projectId + "/Package/" + id)
            .pipe(
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    getReturns(): Observable<Return[]> {
        this.showSpinner();
        return this.http
            .get(this.getApiUrl() + "/" + this.projectId + "/returns")
            .pipe(
                map((res) => res as Return[]),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    getReturn(id: number): Observable<Return> {
        this.showSpinner();
        return this.http
            .get(this.getApiUrl() + "/" + this.projectId + "/returns/" + id)
            .pipe(
                map((res) => res as Return),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    uploadFile(formData: FormData): Observable<any> {
        this.showSpinner();
        return this.http.post(this.getApiUrl() + "/upload", formData).pipe(
            catchError(this.handleError),
            finalize(() => this.hideSpinner())
        );
    }

    getRecipientNameBySecurityCode(securityCode: string): Observable<any> {
        return this.http
            .get(
                this.getApiUrl() +
                    "/" +
                    this.projectId +
                    "/securityCode/" +
                    securityCode
            )
            .pipe(
                map((res) => res),
                catchError(this.handleError),
                finalize(() => {})
            );
    }

    getAttachments(
        projectId: number,
        aggregateId: string,
        type: string
    ): Observable<Attachment[]> {
        return this.http
            .get(
                this.getApiUrl() +
                    "/" +
                    projectId +
                    "/" +
                    type +
                    "/" +
                    aggregateId
            )
            .pipe(
                map((res) => res as Attachment[]),
                catchError(this.handleError)
            );
    }

    getAggregateId(): Observable<string> {
        return this.http.get(this.getApiUrl() + "/aggregateId").pipe(
            map((res) => {
                return res;
            }),
            catchError(this.handleError)
        );
    }

    private mapSave(order: any) {
        order.answers = [];

        for (let prop in order) {
            let splitProp = prop.split("_");

            if (splitProp.length == 2) {
                let answer = new SaveSubAnswer();

                answer.subContractorOrderId = order.id;
                answer.questionId = +splitProp[1];
                answer.answer = order[prop];

                order.answers.push(answer);
            }
        }

        for (let prop in order) {
            let splitProp = prop.split("_");
            let answer = order.answers.find(
                (s) => s.questionId == +splitProp[1]
            );

            if (!answer) continue;

            if (splitProp.length == 3) {
                answer.other = order[prop];
            } else if (splitProp.length === 4) {
                answer.signatureByThirdParty = order[prop];
            }
        }

        return order;
    }
}

export class SubContractor {
    id: number;
    name: string;
    suffix: string;
    address: string;
    code: string;
    type: string;
    size: string;
    location: string;

    static map(entity: any): SubContractor {
        return {
            address: entity.address,
            id: entity.id,
            name: entity.name,
            suffix: entity.suffix,
            code: entity.code,
            type: entity.type,
            size: entity.size,
            location: entity.location,
        };
    }
}

class SaveSubAnswer {
    id: number;
    subContractorOrderId: number;
    questionId: number;
    answer: any[];
    other: string;
    signatureByThirdParty: string;
}

export class Order {
    id: number;
    subContractorId: number;
    projectId: number;
    enteredById: number;
    dateEntered: Date;
    dateAnswered: Date;
    entryNumber: number;
    copyToId: number[];
    package: string;
    type: string;
    typeOther: string;
    retention: number;
    contractSum: number;
    budgetSum: number;
    accountContactId: number;
    siteContactId: number;
    specialAgreements: string;
    orderNumber: string;
    paymentTerms: string;
    valuationsIssuedBy: string;
    estimatedStartDate: Date;
    enteredByFullName?: string;
    subContractorName?: string;
    copiedToNames?: string[];
    copiedToNamesString?: string;
    subContractorCode?: string;
    subContractorType?: string;
    address?: string;
    accountContact?: string;
    accountContactEmail?: string;
    accountContactPhone?: string;
    siteContact?: string;
    siteContactEmail?: string;
    siteContactPhone?: string;

    answersToDisplay: any[];
    answers: any[];
    files: any[];

    static map(entity: any): Order {
        if (!entity) return new Order();

        return {
            contractSum: entity.contractSum,
            dateAnswered: entity.dateAnswered,
            dateEntered: entity.dateEntered,
            enteredById: entity.enteredById,
            id: entity.id,
            accountContactId: entity.accountContactId,
            siteContactId: entity.siteContactId,
            paymentTerms: entity.paymentTerms,
            orderNumber: entity.orderNumber,
            valuationsIssuedBy: entity.valuationsIssuedBy,
            estimatedStartDate: entity.estimatedStartDate,
            package: entity.package,
            copyToId: entity.copyToId,
            projectId: entity.projectId,
            retention: entity.retention,
            budgetSum: entity.budgetSum,
            specialAgreements: entity.specialAgreements,
            subContractorId: entity.subContractorId,
            type: entity.type,
            typeOther: entity.typeOther,
            answers: entity.answers,
            answersToDisplay: entity.answersToDisplay,
            files: entity.files,
            entryNumber: entity.entryNumber,
        };
    }
}

export class Payment {
    id: number;
    subContractorId: number;
    projectId: number;
    enteredById: number;
    status: string;
    commissionStatus: string;
    projectLogoUrl: string;
    dateEntered: Date;
    dateOfClaim: Date;
    package: string;
    orderNo: string;
    contractValue: number;
    retention: number;
    amountClaimedInValuation: number;
    amountPaidInValuation: number;
    amountClaimedToDate: number;
    amountPaidToDate: number;
    amountInDispute: number;
    amountRetentionPaid: number;
    amountGross: number = 0;
    agreements: string;
    notifyToId: number[];
    lines: any[];
    previousPayments: any[];
    files: any[];
    project: any;
    subcontractor: any;
    enteredByFullName?: string;
    subContractorName?: string;
    subContractorCode?: string;
    subContractorType?: string;

    static map(entity: any): Payment {
        if (!entity) return new Payment();

        return {
            agreements: entity.agreements,
            previousPayments: entity.previousPayments,
            notifyToId: entity.notifyToId,
            projectLogoUrl: entity.projectLogoUrl,
            amountGross: entity.amountGross,
            contractValue: entity.contractValue,
            commissionStatus: entity.commissionStatus,
            lines: entity.lines,
            files: entity.files,
            project: entity.project,
            subcontractor: entity.subcontractor,
            amountRetentionPaid: entity.amountRetentionPaid,
            orderNo: entity.orderNo,
            retention: entity.retention,
            amountClaimedInValuation: entity.amountClaimedInValuation,
            amountClaimedToDate: entity.amountClaimedToDate,
            amountInDispute: entity.amountInDispute,
            amountPaidInValuation: entity.amountPaidInValuation,
            amountPaidToDate: entity.amountPaidToDate,
            dateOfClaim: entity.dateOfClaim,
            status: entity.status,
            dateEntered: entity.dateEntered,
            enteredById: entity.enteredById,
            id: entity.id,
            package: entity.package,
            projectId: entity.projectId,
            subContractorId: entity.subContractorId,
        };
    }
}

export class AllAttachmentsDto {
    aggregateId: string;
    type: string;
    projectId: number;
}

export class Package {
    id: number;
    entryNumber: number;
    projectId: number;
    name: string;
    projectName: string;
    recipientName: string;
    dateEntered: Date;
    enteredById: number;
    enteredByFullName: string;
    subContractorPackageContactIds: number[];
    clientsConsultantsContactsIds: number[];
    suppliersIds: number[];
    isResponseRequired: boolean;
    isResponseRequiredFlag: string;
    isPackageReceived: boolean = false;
    instructions: string;
    subContractorPackageAttachments: Attachment[];
    subcontractorPrice: number;
    pricedAt: Date;
    receivedAt: Date;
    securityCode: string;
    comments: string;
    revisionNumber: number;
    aggregateId: string;
    clientName: string;
    managerIds: number[];
    managers: string[];

    static map(entity: any): Package {
        if (!entity) return new Package();

        var subContractorPackageAttachments: Attachment[] = [];

        (entity.subContractorPackageAttachments || []).forEach(function (
            attachment
        ) {
            subContractorPackageAttachments.push(Attachment.map(attachment));
        });

        return {
            dateEntered: entity.dateEntered,
            enteredByFullName: entity.enteredByFullName,
            enteredById: entity.enteredById,
            id: entity.id,
            name: entity.name,
            projectId: entity.projectId,
            subContractorPackageAttachments: subContractorPackageAttachments,
            subContractorPackageContactIds:
                entity.subContractorPackageContactIds,
            clientsConsultantsContactsIds: entity.clientsConsultantsContactsIds,
            suppliersIds: entity.suppliersIds,
            isResponseRequired: entity.isResponseRequired,
            isResponseRequiredFlag: entity.isResponseRequired ? "Yes" : "No",
            instructions: entity.instructions,
            isPackageReceived: entity.isPackageReceived || false,
            subcontractorPrice: entity.subcontractorPrice,
            pricedAt: entity.pricedAt,
            receivedAt: entity.receivedAt,
            projectName: entity.projectName,
            recipientName: "",
            securityCode: entity.securityCode,
            comments: entity.comments,
            revisionNumber: entity.revisionNumber,
            aggregateId: entity.aggregateId,
            clientName: entity.clientName,
            entryNumber: entity.entryNumber,
            managerIds: entity.managerIds,
            managers: entity.managers,
        };
    }
}

export class Attachment {
    id: number;
    subContractorPackageId: number;
    type: string;
    isFolder: boolean;
    fileName: string;
    fileUrl: string;
    selected: boolean = false;
    added: Date;
    addedById: number;
    addedByFullName: string;
    originalPath: string;
    revisionNumber: number;
    newAttachment: boolean = false;
    isUploaded: boolean = false;

    static map(entity: any): Attachment {
        if (!entity) return new Attachment();

        return {
            fileName: entity.fileName,
            fileUrl: entity.fileUrl,
            id: entity.id,
            isFolder: entity.isFolder,
            subContractorPackageId: entity.subContractorPackageId,
            type: entity.type,
            added: entity.added,
            addedById: entity.addedById,
            addedByFullName: entity.addedByFullName,
            selected: entity.selected,
            newAttachment: entity.newAttachment,
            originalPath: entity.originalPath,
            revisionNumber: entity.revisionNumber,
            isUploaded: entity.isUploaded,
        };
    }
}

export class Return {
    id: number;
    packageName: string;
    packageId: number;
    recipientName: string;
    responseDate: Date;
    securityCode: string;
    subcontractorPackageId: number;

    static map(entity: any): Return {
        if (!entity) return new Return();

        return {
            id: entity.id,
            packageName: entity.packageName,
            packageId: entity.packageId,
            recipientName: entity.recipientName,
            responseDate: entity.responseDate,
            securityCode: entity.securityCode,
            subcontractorPackageId: entity.subcontractorPackageId,
        };
    }
}
